import axios from 'axios'
import { keyBy } from 'lodash'
import { flow } from 'lodash/fp'
import { combineReducers, Reducer } from 'redux'
import { call, cancelled } from 'typed-redux-saga'
import {
  AccountLinkingRequestStatusEnum,
  getAccountLinkingRequests,
  IAccountLinkingRequest
} from '../../../../../../../../api/dynamics'
import {
  OdataFilterOperatorEnum,
  OdataPropertyFilterGroup
} from '../../../../../../../../api/odata'
import { IOdataRequest } from '../../../../../../../../api/odata.types'
import {
  IListsFacetFilter,
  IListsFilter
} from '../../../../../../../../features/Lists/core/contracts/IListsFilter'
import { IOdataListDataState } from '../../../../../../../../features/OdataList/common/IOdataListDataState'
import { IOdataListUiState } from '../../../../../../../../features/OdataList/common/IOdataListUiState'
import { convertColumnTypeToFilterType } from '../../../../../../../../features/OdataList/common/service'
import {
  IOdataListColumnDefinition,
  IWithGetValue
} from '../../../../../../../../features/OdataList/common/types'
import { createOdataListDataStore } from '../../../../../../../../features/OdataList/store/odataListDataStore'
import { createOdataListUiStore } from '../../../../../../../../features/OdataList/store/odataListUiStore'
import { IOdataResult } from '../../../../../../../../shared/contracts/IOdataResult'
import { AppState } from '../../../../../../../../store'
import { getDynamicsApiOptions } from '../../../../../../../../store/shared/sagas'

export type AccountLinkingRequestListColumnName =
  | 'Client Name'
  | 'Client Role'
  | 'Status'
  | 'Wealthscape Id'
  | 'Request Type'
  | 'Accounts'
  | 'Verification'
  | 'Last Modified'
  | 'Created'
  | 'Team'
  | 'ID'

export interface IAccountLinkingRequestListColumnDefinition
  extends IOdataListColumnDefinition,
    IWithGetValue<IAccountLinkingRequest> {
  name: AccountLinkingRequestListColumnName
}

const defaultColumn: Partial<IAccountLinkingRequestListColumnDefinition> = {
  filterable: true,
  sortable: true
}

export const accountLinkingRequestListColumns: IAccountLinkingRequestListColumnDefinition[] =
  [
    {
      ...defaultColumn,
      name: 'Client Name',
      dataPath: 'rcm_newclient',
      type: 'string',
      width: 250,
      searchFields: ['rcm_newclient', 'rcm_newclientemailid'],
      getValue: ({ rcm_newclient }) => rcm_newclient
    },
    {
      ...defaultColumn,
      name: 'Client Role',
      dataPath: 'rcm_newclientrole',
      type: 'string',
      width: 120,
      getValue: ({ rcm_newclientrole }) => rcm_newclientrole
    },
    {
      ...defaultColumn,
      name: 'Status',
      dataPath: 'rcm_status',
      type: 'string',
      width: 120,
      facetable: true,
      getValue: ({ rcm_status }) => rcm_status
    },
    {
      ...defaultColumn,
      name: 'Wealthscape Id',
      dataPath: 'rcm_cliendid',
      type: 'string',
      width: 120,
      getValue: ({ rcm_cliendid }) => rcm_cliendid
    },
    {
      ...defaultColumn,
      name: 'Request Type',
      dataPath: 'rcm_requesttype',
      type: 'string',
      width: 100,
      facetable: true,
      getValue: ({ rcm_requesttype }) => rcm_requesttype
    },
    {
      ...defaultColumn,
      name: 'Accounts',
      dataPath: 'rcm_accountstoadd',
      type: 'string',
      width: 100,
      searchFields: ['rcm_accountstoadd'],
      sortable: false,
      getValue: ({ rcm_accountstoadd }) => rcm_accountstoadd
    },
    {
      ...defaultColumn,
      name: 'Verification',
      dataPath: 'Verification',
      type: 'string',
      width: 150,
      filterable: false,
      sortable: false,
      getValue: ({
        rcm_verificationdate,
        rcm_verificationtime,
        rcm_client,
        rcm_clientmethod
      }) =>
        [
          rcm_verificationdate,
          rcm_verificationtime,
          rcm_client,
          rcm_clientmethod
        ].join(',')
    },
    {
      ...defaultColumn,
      name: 'Last Modified',
      dataPath: 'modifiedon',
      type: 'date',
      width: 150,
      getValue: ({ modifiedon }) => modifiedon
    },
    {
      ...defaultColumn,
      name: 'Created',
      dataPath: 'createdon',
      type: 'date',
      width: 150,
      getValue: ({ createdon }) => createdon
    },
    {
      ...defaultColumn,
      name: 'Team',
      dataPath: 'owningbusinessunit/name',
      type: 'string',
      width: 150,
      getValue: ({ owningbusinessunit }) => owningbusinessunit?.name,
      expand: ['owningbusinessunit($select=name)']
    },
    {
      ...defaultColumn,
      name: 'ID',
      dataPath: 'rcm_accountlinkingrequestid',
      type: 'string',
      width: 150,
      getValue: ({ rcm_accountlinkingrequestid }) =>
        rcm_accountlinkingrequestid,
      hidden: true
    }
  ]

const fetchAccountLinkingRequests = function* (
  request: IOdataRequest,
  chunks?: IOdataResult<IAccountLinkingRequest>[]
) {
  // eslint-disable-next-line import/no-named-as-default-member
  const source = axios.CancelToken.source()
  const apiOptions = yield* call(getDynamicsApiOptions)
  const [last] = chunks?.slice(-1) || []
  const next = last?.['@odata.nextLink']

  try {
    if (next) {
      const fetchRequests = () =>
        axios
          .get<IOdataResult<IAccountLinkingRequest>>(next, {
            headers: {
              Authorization: `Bearer ${apiOptions.accessToken}`,
              ...(request.top && {
                Prefer: `odata.maxpagesize=${request.top}, odata.include-annotations="OData.Community.Display.V1.FormattedValue"`
              })
            }
          })
          .then((x) => x.data)
      return yield* call(fetchRequests)
    }
    const result = yield* call(getAccountLinkingRequests, apiOptions, request)
    return result
  } finally {
    if (yield* cancelled()) {
      source.cancel()
    }
  }
}

const rootListSelector = (state: AppState) =>
  state.modules.advisory.modules.clients.modules.accountLinkingRequests.features
    .accountLinkingRequestList

const dataStore = createOdataListDataStore(
  '@modules/@advisory/@modules/@accountlinkingrequest',
  fetchAccountLinkingRequests,
  flow(rootListSelector, ({ data }) => data)
)
export const {
  actions: accountLinkingRequestListDataActions,
  selectors: accountLinkingRequestListDataSelectors
} = dataStore
const uiFilters = keyBy(
  accountLinkingRequestListColumns
    .filter((x) => x.filterable)
    .map((column): IListsFilter => {
      const base = {
        id: column.name,
        name: column.name,
        type: convertColumnTypeToFilterType(column),
        dataPath: column.dataPath,
        hasValue: false
      }
      if (column.facetable && column.name === 'Request Type') {
        return {
          ...base,
          facets: [{ value: 'existing' }, { value: 'new' }, { value: 'delink' }]
        } as IListsFacetFilter
      }
      if (column.filterable && column.facetable) {
        return {
          ...base,
          facets: Object.entries(AccountLinkingRequestStatusEnum)
            .filter((key) => !(parseInt(key[0]) >= 0))
            .map(([key]) => ({ value: key }))
        } as IListsFacetFilter
      }

      return base
    }),
  ({ id }) => id
)
const convertStatusColumnFilterToOdataFilter = (
  filter: IListsFilter
): OdataPropertyFilterGroup | undefined => {
  const { dataPath, values } = filter as IListsFacetFilter
  const hasValue = values && values.length > 0

  if (!hasValue || !dataPath) {
    return
  }

  return {
    and: [
      {
        operator: OdataFilterOperatorEnum.dynamicsin,
        value: Object.entries(AccountLinkingRequestStatusEnum)
          .filter(([key]) => values?.includes(key))
          .map(([, value]) => value as string),
        path: dataPath,
        type: 'number'
      }
    ]
  }
}
const convertRequestTypeColumnFilterToOdataFilter = (
  filter: IListsFilter
): OdataPropertyFilterGroup | undefined => {
  const { dataPath, values } = filter as IListsFacetFilter
  const hasValue = values && values.length > 0
  if (!hasValue || !dataPath) {
    return
  }

  return {
    and: [
      {
        operator: OdataFilterOperatorEnum.eq,
        value: values?.[0],
        path: dataPath,
        type: 'string'
      }
    ]
  }
}

const uiStore = createOdataListUiStore({
  prefix: '@modules/@advisory/@modules/@accountlinkingrequest',
  initialState: {
    columns: accountLinkingRequestListColumns,
    filters: uiFilters,
    sortBy: { direction: 'desc', name: 'Last Modified' }
  },
  rootSelector: flow(rootListSelector, (x) => x.ui),
  dataStore,
  onConvertToOdataFilter: (filter) => {
    const filterName = filter.name as AccountLinkingRequestListColumnName
    if (filterName !== 'Status') {
      return convertRequestTypeColumnFilterToOdataFilter(filter)
    }

    return convertStatusColumnFilterToOdataFilter(filter)
  }
})

export const {
  selectors: accountLinkingRequestListUiSelectors,
  actions: accountLinkingRequestListUiActions
} = uiStore
export const accountLinkingRequestListReducer: Reducer<{
  data: IOdataListDataState<IAccountLinkingRequest>
  ui: IOdataListUiState
}> = combineReducers({
  data: dataStore.reducer,
  ui: uiStore.reducer
})

export const accountLinkingRequestListSagas = [
  ...dataStore.sagas,
  ...uiStore.sagas
]
